import { qportalApi } from './qportal-api';
import { errorCode } from './admin-security-api';
// Admin mail actions, MCP mail confirmations and MCP activity (openspec/changes/add-admin-mail-actions-and-mcp-audit,
// task Q-E). Built against that change's api-contract.md (2026-11-04, as of commit a081911), which wins over design.md §2,
// §6.3, §9 and §10. Every new endpoint and every new response field name is read in this file only.
//
// No request carries a sender, Reply-To, e-mail address, CC, BCC or attachment: the API has no such field (design §2).
// Admin and SuperAdmin for mail; SuperAdmin for MCP activity. Hugo's D3 (2026-11-04): no portal step-up for resend or
// custom mail, so no request here carries a step-up grant.
export const ADMIN_MAIL_CONTRACT = 'api-contract.md 2026-11-04 (a081911)';
// ---- tolerant readers: API values become strings or null, unknown fields are dropped -------------------------------
const text = (value) => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '');
const optional = (value) => (typeof value === 'string' && value ? value : null);
const count = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);
// A preview without everything a send needs (version, digest, recipient, sender, subject, full text) is no preview.
export const readPreview = (raw) => {
    const preview = {
        kind: text(raw?.kind),
        recipientUserId: text(raw?.recipientUserId),
        recipientName: text(raw?.recipientName),
        recipientEmail: text(raw?.recipientEmail),
        definitionKey: text(raw?.definitionKey),
        definitionName: text(raw?.definitionName),
        versionId: text(raw?.versionId),
        from: text(raw?.from),
        replyTo: optional(raw?.replyTo),
        subject: text(raw?.subject),
        heading: optional(raw?.heading),
        bodyText: text(raw?.bodyText),
        html: text(raw?.html),
        contentDigest: text(raw?.contentDigest),
        originalMessageId: optional(raw?.originalMessageId),
        originalState: optional(raw?.originalState)
    };
    const complete = [preview.recipientUserId, preview.recipientName, preview.recipientEmail, preview.definitionKey, preview.versionId,
        preview.from, preview.subject, preview.bodyText, preview.contentDigest].every((value) => value.trim() !== '');
    return complete ? preview : null;
};
const readSent = (raw) => ({ messageId: text(raw?.messageId), state: text(raw?.state) || 'Queued' });
const readRecipients = (raw) => (Array.isArray(raw) ? raw : Array.isArray(raw?.items) ? raw.items : [])
    .map((item) => ({
    userId: text(item?.userId), name: text(item?.name), email: text(item?.email), role: text(item?.role), status: text(item?.status),
    suppressed: item?.suppressed === true
}))
    .filter((item) => item.userId && item.name);
export const readMessageMail = (row) => {
    const send = row?.adminSend;
    return {
        resendable: row?.resendable === true,
        notResendableReason: optional(row?.notResendableReason),
        adminSend: send && typeof send === 'object'
            ? { kind: text(send.kind), actorName: text(send.actorName), source: text(send.source), createdUtc: text(send.createdUtc), originalMessageId: optional(send.originalMessageId) }
            : null
    };
};
const asciiHost = (host) => {
    try {
        return new URL(`https://${host}`).hostname.toLowerCase() || null;
    }
    catch {
        return null;
    }
};
const limit = (value, fallback) => (typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback);
// Links in a custom mail may only point at ResendConfiguration:AllowedLinkHosts; e-mail addresses only at Reply-To.
export const readCapability = (raw) => {
    const hosts = Array.isArray(raw?.allowedLinkHosts) ? raw.allowedLinkHosts.filter((h) => typeof h === 'string') : [];
    return {
        resendEnabled: raw?.resendEnabled === true,
        customEnabled: raw?.customEnabled === true,
        sendTimeSubject: raw?.sendTimeSubject === true,
        from: text(raw?.from),
        rules: {
            hosts: [...new Set(hosts.map(asciiHost).filter((h) => !!h))],
            replyTo: optional(raw?.replyTo),
            subjectMin: limit(raw?.subject?.min, 3),
            subjectMax: limit(raw?.subject?.max, 150),
            bodyMin: limit(raw?.body?.min, 1),
            bodyMax: limit(raw?.body?.max, 4000),
            bodyMaxLines: limit(raw?.body?.maxLines, 50)
        }
    };
};
export const ADMIN_MAIL_OFF = readCapability({});
// The kill switch is off (ADMIN_MAIL_DISABLED) or the API predates the feature (a 404 without a backend code;
// an unknown message is 404 MESSAGE_NOT_FOUND and is not "off").
export const adminMailSwitchedOff = (error) => {
    const code = errorCode(error);
    return code === 'ADMIN_MAIL_DISABLED' || (error?.response?.status === 404 && code === 'REQUEST_FAILED');
};
// Extra detail some refusals carry: NOT_RESENDABLE / RESEND_LIMIT a reason, LINK_NOT_ALLOWED / ADDRESS_NOT_ALLOWED the
// token, every content refusal the field it is about (api-contract §6).
export const adminMailErrorDetail = (error) => {
    const data = error?.response?.data;
    const field = data?.field === 'subject' || data?.field === 'body' ? data.field : null;
    return { reason: optional(data?.reason), token: optional(data?.token), field };
};
// Retry this send with the same request key (api-contract §5, §6): no answer at all, or any server error (5xx, such as
// MAIL_OUTCOME_UNKNOWN, MAIL_BUSY or INTERNAL_ERROR). The mail may then already be queued, and the same key returns that
// first result. Two 5xx refusals are certain that nothing was queued (the kill switch, the definition off) and, like every
// 4xx refusal, give the next attempt a new key.
const nothingQueued5xx = ['ADMIN_MAIL_DISABLED', 'MAIL_UNAVAILABLE'];
export const retryWithSameKey = (error) => {
    const status = error?.response?.status;
    return !status || (status >= 500 && !nothingQueued5xx.includes(errorCode(error)));
};
// 8-64 characters [A-Za-z0-9_-] (api-contract §5): a UUID.
export const newRequestKey = () => {
    const source = globalThis.crypto;
    if (typeof source?.randomUUID === 'function')
        return source.randomUUID();
    const bytes = new Uint8Array(16);
    if (typeof source?.getRandomValues === 'function')
        source.getRandomValues(bytes);
    else
        for (let i = 0; i < 16; i++)
            bytes[i] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
const emails = 'admin/emails/';
const segment = (id) => encodeURIComponent(id);
export const AdminMailApi = {
    capability: async (signal) => readCapability((await qportalApi.get(emails + 'admin-mail', { signal })).data),
    resendPreview: async (messageId) => readPreview((await qportalApi.post(emails + `messages/${segment(messageId)}/resend/preview`, {})).data),
    // The digest and version of the preview the admin saw: the API refuses TEMPLATE_CHANGED / CONTENT_CHANGED otherwise.
    resend: async (messageId, preview, requestKey) => readSent((await qportalApi.post(emails + `messages/${segment(messageId)}/resend`, {
        expectedVersionId: preview.versionId, contentDigest: preview.contentDigest, requestKey
    })).data),
    recipients: async (search, signal) => readRecipients((await qportalApi.get(emails + 'recipients', { params: { search }, signal })).data),
    customPreview: async (input) => readPreview((await qportalApi.post(emails + 'custom/preview', { recipientUserId: input.recipientUserId, subject: input.subject, body: input.body })).data),
    sendCustom: async (input, preview, requestKey) => readSent((await qportalApi.post(emails + 'custom', {
        recipientUserId: input.recipientUserId, subject: input.subject, body: input.body,
        expectedVersionId: preview.versionId, contentDigest: preview.contentDigest, requestKey
    })).data)
};
export const readDecisionMail = (raw) => {
    if (!raw || typeof raw !== 'object')
        return null;
    const recipient = raw.recipient && typeof raw.recipient === 'object' ? raw.recipient : {};
    const template = raw.template && typeof raw.template === 'object' ? raw.template : {};
    const original = raw.originalMessage && typeof raw.originalMessage === 'object' ? raw.originalMessage : null;
    const mail = {
        mailKind: text(raw.mailKind ?? raw.kind),
        recipientName: text(recipient.name),
        recipientEmail: text(recipient.email),
        templateKey: text(template.key),
        templateName: text(template.name),
        from: text(raw.from),
        replyTo: optional(raw.replyTo),
        subject: text(raw.subject),
        heading: optional(raw.heading),
        bodyText: text(raw.bodyText),
        original: original ? { messageId: text(original.messageId), createdUtc: text(original.createdUtc), state: text(original.state) } : null,
        messageId: optional(raw.messageId),
        contentAvailable: raw.contentAvailable !== false
    };
    const complete = mail.contentAvailable && (mail.mailKind === 'resend' || mail.mailKind === 'custom') &&
        [mail.recipientName, mail.recipientEmail, mail.templateKey, mail.from, mail.subject, mail.bodyText].every((value) => value.trim() !== '');
    return { ...mail, complete };
};
// Only these fields are ever read, so a field the API should not send (RequestHash, arguments) is never shown.
const readActivityItem = (item) => ({
    occurredUtc: text(item?.occurredUtc), userId: text(item?.userId), userName: text(item?.userName), role: text(item?.role),
    clientId: text(item?.clientId), clientName: text(item?.clientName), resource: text(item?.resource), tool: text(item?.tool),
    targetType: text(item?.targetType), targetId: text(item?.targetId), outcomeCode: text(item?.outcomeCode),
    durationMs: count(item?.durationMs), correlationId: text(item?.correlationId), write: item?.write === true
});
const readActivityPage = (raw, filter) => {
    const items = (Array.isArray(raw) ? raw : Array.isArray(raw?.items) ? raw.items : []).map(readActivityItem);
    return {
        items,
        page: count(raw?.page) || filter.page,
        pageSize: count(raw?.pageSize) || filter.pageSize,
        totalCount: typeof raw?.totalCount === 'number' ? raw.totalCount : items.length
    };
};
const readSummary = (raw) => (Array.isArray(raw) ? raw : Array.isArray(raw?.items) ? raw.items : []).map((row) => ({
    userId: text(row?.userId), userName: text(row?.userName), role: text(row?.role), clientId: text(row?.clientId),
    clientName: text(row?.clientName), calls: count(row?.calls), errors: count(row?.errors), writeCalls: count(row?.writeCalls),
    lastUtc: text(row?.lastUtc)
}));
const clean = (params) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));
// MCP activity exists on this API unless the route is missing (a 404 without a backend code: an API before this change).
export const mcpActivityMissing = (error) => error?.response?.status === 404 && errorCode(error) === 'REQUEST_FAILED';
export const McpActivityApi = {
    list: async (filter, signal) => readActivityPage((await qportalApi.get('admin/mcp-activity', { params: clean(filter), signal })).data, filter),
    summary: async (window, signal) => readSummary((await qportalApi.get('admin/mcp-activity/summary', { params: clean(window), signal })).data)
};
